import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  Stack,
  Alert,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  AccessTime as ClockIcon,
} from '@mui/icons-material';
import AttendanceService, { Attendance } from '../../services/attendance.service';
import { NumberTextField } from '../../components/common';
import EmployeeService from '../../services/employee.service';

const AttendanceForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [employees, setEmployees] = useState<any[]>([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<any>(null);

  const [formData, setFormData] = useState<Attendance>({
    employee: 0,
    date: new Date().toISOString().split('T')[0],
    clockIn: new Date().toTimeString().slice(0, 5),
    clockOut: '',
    status: 'present',
    notes: '',
    workHours: 0,
    overtimeHours: 0,
    location: '',
  });

  useEffect(() => {
    fetchEmployees();
    if (isEdit && id) {
      fetchAttendance(parseInt(id));
    }
  }, [id, isEdit]);

  const fetchEmployees = async () => {
    try {
      setEmployeesLoading(true);
      const response = await EmployeeService.getAll({
        'pagination[pageSize]': 100,
        'filters[status][$eq]': 'active',
      });
      setEmployees(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setEmployeesLoading(false);
    }
  };

  const convertTimeToInputFormat = (time: string): string => {
    // Convert HH:mm:ss.SSS to HH:mm for HTML time input
    if (!time) return '';
    return time.substring(0, 5); // Take only HH:mm part
  };

  const fetchAttendance = async (attendanceId: number) => {
    try {
      setLoading(true);
      const response = await AttendanceService.getOne(attendanceId);
      const data = response.data?.data;
      
      if (data) {
        const attrs = data.attributes;
        setFormData({
          employee: attrs.employee?.data?.id || 0,
          date: attrs.date,
          clockIn: convertTimeToInputFormat(attrs.clockIn),
          clockOut: convertTimeToInputFormat(attrs.clockOut || ''),
          status: attrs.status || 'present',
          notes: attrs.notes || '',
          workHours: attrs.workHours || 0,
          overtimeHours: attrs.overtimeHours || 0,
          location: attrs.location || '',
        });

        // Set selected employee for autocomplete
        if (attrs.employee?.data) {
          setSelectedEmployee(attrs.employee.data);
        }
      }
    } catch (error: any) {
      console.error('Error fetching attendance:', error);
      setError('Failed to load attendance record');
    } finally {
      setLoading(false);
    }
  };

  const calculateWorkHours = (clockIn: string, clockOut: string): number => {
    if (!clockIn || !clockOut) return 0;

    const [inHour, inMinute] = clockIn.split(':').map(Number);
    const [outHour, outMinute] = clockOut.split(':').map(Number);

    const inMinutes = inHour * 60 + inMinute;
    const outMinutes = outHour * 60 + outMinute;

    let diffMinutes = outMinutes - inMinutes;
    if (diffMinutes < 0) {
      diffMinutes += 24 * 60; // Handle next day
    }

    return Math.round((diffMinutes / 60) * 100) / 100; // Round to 2 decimals
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const newFormData = { ...formData, [name]: value };

    // Auto-calculate work hours when clockIn or clockOut changes
    if ((name === 'clockIn' || name === 'clockOut') && newFormData.clockIn && newFormData.clockOut) {
      const hours = calculateWorkHours(newFormData.clockIn, newFormData.clockOut);
      newFormData.workHours = hours;

      // Calculate overtime (assuming 8 hours is standard)
      const standardHours = 8;
      newFormData.overtimeHours = hours > standardHours ? hours - standardHours : 0;
    }

    setFormData(newFormData);
  };

  const handleEmployeeChange = (event: any, newValue: any) => {
    setSelectedEmployee(newValue);
    setFormData({ ...formData, employee: newValue?.id || 0 });
  };

  const validateForm = (): boolean => {
    if (!formData.employee) {
      setError('Please select an employee');
      return false;
    }
    if (!formData.date) {
      setError('Please select a date');
      return false;
    }
    if (!formData.clockIn) {
      setError('Please enter clock in time');
      return false;
    }
    return true;
  };

  const convertTimeFormat = (time: string): string => {
    // Convert to HH:mm:ss.SSS format for Strapi
    if (!time || !time.trim()) return '';
    
    // Remove any whitespace
    time = time.trim();
    
    // If already in HH:mm:ss.SSS format, return as is
    if (/^\d{2}:\d{2}:\d{2}\.\d{3}$/.test(time)) {
      return time;
    }
    
    // If in HH:mm:ss format, add milliseconds
    if (/^\d{2}:\d{2}:\d{2}$/.test(time)) {
      return `${time}.000`;
    }
    
    // If in HH:mm format, add seconds and milliseconds
    if (/^\d{2}:\d{2}$/.test(time)) {
      return `${time}:00.000`;
    }
    
    // If it has milliseconds but wrong format, try to fix it
    if (time.includes('.')) {
      const parts = time.split('.');
      if (parts.length === 2) {
        const timePart = parts[0];
        if (/^\d{2}:\d{2}:\d{2}$/.test(timePart)) {
          return `${timePart}.${parts[1].padEnd(3, '0').substring(0, 3)}`;
        }
        if (/^\d{2}:\d{2}$/.test(timePart)) {
          return `${timePart}:00.${parts[1].padEnd(3, '0').substring(0, 3)}`;
        }
      }
    }
    
    // Default: assume HH:mm and add :00.000
    return `${time}:00.000`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      // Convert time formats and prepare submit data
      const clockInFormatted = convertTimeFormat(formData.clockIn);
      if (!clockInFormatted) {
        setError('Clock in time is required');
        setLoading(false);
        return;
      }

      const submitData: any = {
        employee: formData.employee,
        date: formData.date,
        clockIn: clockInFormatted,
        status: formData.status,
        workHours: formData.workHours || 0,
        overtimeHours: formData.overtimeHours || 0,
      };

      // Only include clockOut if it's provided
      if (formData.clockOut && formData.clockOut.trim()) {
        const clockOutFormatted = convertTimeFormat(formData.clockOut);
        if (clockOutFormatted) {
          submitData.clockOut = clockOutFormatted;
        }
      }

      // Include optional fields only if they have values
      if (formData.location && formData.location.trim()) {
        submitData.location = formData.location.trim();
      }
      if (formData.notes && formData.notes.trim()) {
        submitData.notes = formData.notes.trim();
      }

      if (isEdit && id) {
        await AttendanceService.update(parseInt(id), submitData);
        setSuccess('Attendance updated successfully!');
      } else {
        await AttendanceService.create(submitData);
        setSuccess('Attendance recorded successfully!');
      }

      setTimeout(() => {
        navigate('/attendance');
      }, 1500);
    } catch (error: any) {
      console.error('Error saving attendance:', error);
      setError(
        error.response?.data?.error?.message ||
        'Failed to save attendance. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleQuickClockIn = () => {
    const now = new Date();
    setFormData({
      ...formData,
      date: now.toISOString().split('T')[0],
      clockIn: now.toTimeString().slice(0, 5),
      status: 'present',
    });
    setSuccess('Clock in time set to now!');
    setTimeout(() => setSuccess(''), 2000);
  };

  const handleQuickClockOut = () => {
    const now = new Date();
    const clockOut = now.toTimeString().slice(0, 5);
    const workHours = calculateWorkHours(formData.clockIn, clockOut);
    const standardHours = 8;
    
    setFormData({
      ...formData,
      clockOut,
      workHours,
      overtimeHours: workHours > standardHours ? workHours - standardHours : 0,
    });
    setSuccess('Clock out time set to now!');
    setTimeout(() => setSuccess(''), 2000);
  };

  if (loading && isEdit) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          {isEdit ? 'Edit Attendance' : 'Record Attendance'}
        </Typography>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            {/* Employee Selection */}
            <Autocomplete
              value={selectedEmployee}
              onChange={handleEmployeeChange}
              options={employees}
              loading={employeesLoading}
              getOptionLabel={(option) =>
                `${option.attributes?.firstName} ${option.attributes?.lastName} (${option.attributes?.employeeNumber})`
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Employee *"
                  required
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {employeesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            {/* Date */}
            <TextField
              fullWidth
              label="Date"
              name="date"
              type="date"
              value={formData.date}
              onChange={handleChange}
              required
              InputLabelProps={{ shrink: true }}
            />

            {/* Clock In/Out Times */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Box flex={1}>
                <Stack direction="row" spacing={1} alignItems="flex-end">
                  <TextField
                    fullWidth
                    label="Clock In"
                    name="clockIn"
                    type="time"
                    value={formData.clockIn}
                    onChange={handleChange}
                    required
                    InputLabelProps={{ shrink: true }}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ClockIcon />}
                    onClick={handleQuickClockIn}
                    sx={{ minWidth: '100px' }}
                  >
                    Now
                  </Button>
                </Stack>
              </Box>

              <Box flex={1}>
                <Stack direction="row" spacing={1} alignItems="flex-end">
                  <TextField
                    fullWidth
                    label="Clock Out"
                    name="clockOut"
                    type="time"
                    value={formData.clockOut}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ClockIcon />}
                    onClick={handleQuickClockOut}
                    disabled={!formData.clockIn}
                    sx={{ minWidth: '100px' }}
                  >
                    Now
                  </Button>
                </Stack>
              </Box>
            </Stack>

            {/* Status */}
            <TextField
              select
              fullWidth
              label="Status"
              name="status"
              value={formData.status}
              onChange={handleChange}
              required
            >
              <MenuItem value="present">Present</MenuItem>
              <MenuItem value="absent">Absent</MenuItem>
              <MenuItem value="late">Late</MenuItem>
              <MenuItem value="halfDay">Half Day</MenuItem>
            </TextField>

            {/* Work Hours and Overtime */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <NumberTextField
                fullWidth
                label="Work Hours"
                name="workHours"
                value={formData.workHours}
                onChange={handleChange}
                inputProps={{ step: '0.01', min: '0' }}
                helperText="Auto-calculated from clock in/out"
              />

              <NumberTextField
                fullWidth
                label="Overtime Hours"
                name="overtimeHours"
                value={formData.overtimeHours}
                onChange={handleChange}
                inputProps={{ step: '0.01', min: '0' }}
                helperText="Hours beyond 8 hours/day"
              />
            </Stack>

            {/* Location */}
            <TextField
              fullWidth
              label="Location"
              name="location"
              value={formData.location}
              onChange={handleChange}
              placeholder="e.g., Office, Remote, Client Site"
            />

            {/* Notes */}
            <TextField
              fullWidth
              label="Notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              multiline
              rows={3}
              placeholder="Any additional notes about this attendance record..."
            />

            {/* Action Buttons */}
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={() => navigate('/attendance')}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={loading}
              >
                {loading ? 'Saving...' : isEdit ? 'Update' : 'Save'}
              </Button>
            </Stack>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default AttendanceForm;

